export type TimesheetStatus = 'open' | 'submitted' | 'approved' | 'rejected';
export type ProjectStatus = 'draft' | 'in_progress' | 'paused' | 'completed' | 'cancelled';

interface StatusBadgeProps {
  status?: TimesheetStatus;
  state?: string;
  size?: 'sm' | 'md' | 'lg';
}

const statusConfig: Record<string, { label: string; className: string }> = {
  open: {
    label: 'Open',
    className: 'bg-gray-100 text-gray-700',
  },
  submitted: {
    label: 'Pending Review',
    className: 'bg-yellow-100 text-yellow-800',
  },
  approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-800',
  },
  rejected: {
    label: 'Needs Revision',
    className: 'bg-red-100 text-red-800',
  },
  draft: {
    label: 'Draft',
    className: 'bg-gray-100 text-gray-700',
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-blue-100 text-blue-800',
  },
  paused: {
    label: 'Paused',
    className: 'bg-yellow-100 text-yellow-800',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-100 text-green-800',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-gray-100 text-gray-500',
  },
  sent: {
    label: 'Sent',
    className: 'bg-blue-100 text-blue-800',
  },
  // Workflow statuses (Phase 4)
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  dispatched: { label: 'Dispatched', className: 'bg-blue-100 text-blue-800' },
  running: { label: 'Running', className: 'bg-blue-100 text-blue-800' },
  succeeded: { label: 'Succeeded', className: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
  dispatch_failed: { label: 'Dispatch Failed', className: 'bg-red-100 text-red-800' },
  timed_out: { label: 'Timed Out', className: 'bg-orange-100 text-orange-800' },
  skipped: { label: 'Skipped', className: 'bg-gray-100 text-gray-500' },
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-sm',
};

export function StatusBadge({ status, state, size = 'md' }: StatusBadgeProps) {
  const key = state || status || 'open';
  const config = statusConfig[key] ?? { label: key, className: 'bg-gray-100 text-gray-700' };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ${config.className} ${sizeClasses[size]}`}
    >
      {config.label}
    </span>
  );
}

// Icon variants for more visual feedback
export function StatusIcon({ status }: { status: TimesheetStatus }) {
  switch (status) {
    case 'open':
      return (
        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      );
    case 'submitted':
      return (
        <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'approved':
      return (
        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'rejected':
      return (
        <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return null;
  }
}
